import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, UserRound } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { useAuth } from "contexts/AuthContext";
import { useDialog } from "contexts/DialogContext";
import { useI18n, LANGUAGE_OPTIONS } from "i18n";
import { ROLE_LABEL_KEY } from "pages/admin/Accounts";
import api from "lib/api";

function Field({ label, value, mono }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className={`text-sm ${mono ? "font-mono" : ""}`}>{value || "-"}</p>
    </div>
  );
}

/** Detalhe de usuario: janela propria (rota), nunca modal. */
export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const { t, tf } = useI18n();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const canManage = !!user?.capabilities?.manage_users;

  const fetchAccount = useCallback(async () => {
    try {
      const res = await api.get(`/api/accounts/${id}/`);
      setAccount(res.data);
    } catch {
      setError(t("account.notFound"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    if (canManage) fetchAccount();
  }, [fetchAccount, canManage]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const languageLabel = (value) => {
    if (!value) return t("account.languageDefault");
    return LANGUAGE_OPTIONS.find((l) => l.value === value)?.label || value;
  };

  const handleDelete = async () => {
    let failed = false;
    const confirmed = await confirm({
      title: t("account.deleteTitle"),
      description: tf("account.deleteBody", {
        name: account.first_name || account.email,
      }),
      variant: "danger",
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/api/accounts/${account.id}/`);
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed) {
      navigate("/app/admin/accounts");
      return;
    }
    if (failed) {
      await alert({
        title: t("common.deleteError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="w-full animate-fade-in">
        <button
          onClick={() => navigate("/app/admin/accounts")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("common.back")}
        </button>
        <Card className="w-full">
          <CardContent className="pt-6">
            <p className="text-sm text-red-500">{error || t("account.notFound")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fullName = [account.first_name, account.last_name].filter(Boolean).join(" ");

  return (
    <div className="w-full animate-fade-in space-y-6">
      <button
        onClick={() => navigate("/app/admin/accounts")}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t("common.back")}
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <UserRound className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{fullName || account.email}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("account.detailsTitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/app/admin/accounts/${account.id}/edit`)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            {t("common.edit")}
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            {t("common.delete")}
          </Button>
        </div>
      </div>

      <Card className="w-full">
        <CardHeader>
          <h2 className="text-lg font-semibold">{t("common.details")}</h2>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <Field label={t("common.email")} value={account.email} />
            <Field label={t("common.firstName")} value={account.first_name} />
            <Field label={t("common.lastName")} value={account.last_name} />
            <Field label={t("common.phone")} value={account.mobile_phone} />
            <Field
              label={t("account.role")}
              value={t(ROLE_LABEL_KEY[account.role] || "account.role.user")}
            />
            <Field label={t("account.language")} value={languageLabel(account.language)} />
            <Field label={t("common.country")} value={account.country} />
            <Field label={t("common.state")} value={account.state} />
            <Field label={t("common.city")} value={account.city} />
            <Field
              label={t("account.status")}
              value={account.enabled ? t("account.active") : t("account.inactive")}
            />
            <Field
              label={t("account.mustChange")}
              value={account.must_change_pass ? t("common.yes") : t("common.no")}
            />
            <Field
              label={t("account.cannotChange")}
              value={account.cannot_change_pass ? t("common.yes") : t("common.no")}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
